import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

// Real profile URLs
const SOCIALS = {
  instagram: "https://www.instagram.com/sibo_institute/",
  tiktok: "https://www.tiktok.com/@sibo.institute",
  youtube: "https://www.youtube.com/@SIBOInstitute",
  pinterest: "https://www.pinterest.com/SIBO_INSTITUTE/",
};

// External / affiliate placeholders — update when the user has real URLs
const EXT = {
  digestiveEnzyme: "#", // TODO: add affiliate URL
};

export default function Links() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await subscribeNewsletter(email, "links-page");
      toast.success("You're in — check your inbox.");
      setEmail("");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Could not subscribe");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="links-page" data-testid="links-page">
      <Helmet>
        <title>SIBO Institute — Start Here</title>
        <meta name="description" content="Everything that helped me heal from SIBO — guides, meal plans, tested products, and one weekly email." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      <div className="frame">
        {/* Header */}
        <svg className="mark" viewBox="0 0 46 46" fill="none" aria-hidden="true">
          <path d="M8 12c6 0 6 8 12 8s6-8 12-8 6 8 12 8" stroke="#1C2E29" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M8 26c6 0 6 8 12 8s6-8 12-8 6 8 12 8" stroke="#B9852F" strokeWidth="2.4" strokeLinecap="round" />
        </svg>

        <p className="eyebrow">SIBO Institute</p>
        <h1 className="wordmark">I fixed my SIBO. Here's how.</h1>
        <p className="bio">
          4 years of guessing, misdiagnoses, and false starts — until I found what actually worked. Here's everything that helped.
        </p>

        <div className="socials" data-testid="links-socials">
          <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram">
            <svg viewBox="0 0 24 24" fill="none" stroke="#1C2E29" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.2" cy="6.8" r="1" /></svg>
          </a>
          <a href={SOCIALS.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok">
            <svg viewBox="0 0 24 24" fill="#1C2E29"><path d="M14 3c.3 2 1.8 3.6 4 3.9v2.6c-1.4 0-2.8-.4-4-1.2v6.4a5 5 0 1 1-4.3-4.9v2.7a2.3 2.3 0 1 0 1.7 2.2V3z" /></svg>
          </a>
          <a href={SOCIALS.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="social-youtube">
            <svg viewBox="0 0 24 24" fill="#1C2E29"><path d="M22 12s0-3-.4-4.4a3 3 0 0 0-2.1-2.1C18.1 5 12 5 12 5s-6.1 0-7.5.5A3 3 0 0 0 2.4 7.6C2 9 2 12 2 12s0 3 .4 4.4a3 3 0 0 0 2.1 2.1C5.9 19 12 19 12 19s6.1 0 7.5-.5a3 3 0 0 0 2.1-2.1C22 15 22 12 22 12Z" /><path d="M10 15.2 15 12l-5-3.2Z" fill="#EEF1EA" /></svg>
          </a>
          <a href={SOCIALS.pinterest} target="_blank" rel="noopener noreferrer" aria-label="Pinterest" data-testid="social-pinterest">
            <svg viewBox="0 0 24 24" fill="#1C2E29"><path d="M12 2a10 10 0 0 0-3.6 19.3c0-.8 0-1.8.3-2.7l1.4-6s-.3-.7-.3-1.7c0-1.6.9-2.8 2.1-2.8 1 0 1.5.7 1.5 1.6 0 1-.6 2.4-1 3.8-.2 1 .5 1.8 1.5 1.8 1.8 0 3.1-2.3 3.1-5 0-2.1-1.5-3.6-4.1-3.6-3 0-4.7 2.2-4.7 4.5 0 .9.3 1.8.8 2.3l.1.3-.3 1.2c0 .2-.2.2-.4.1-1.3-.6-2.1-2.5-2.1-4 0-3.3 2.4-6.3 6.9-6.3 3.6 0 6.4 2.6 6.4 6 0 3.6-2.3 6.5-5.4 6.5-1.1 0-2.1-.6-2.4-1.2l-.7 2.6c-.2 1-.9 2.2-1.3 3A10 10 0 1 0 12 2Z" /></svg>
          </a>
        </div>

        {/* Hero — free guide */}
        <Link to="/article/the-sibo-starter-guide" className="card card--hero" data-testid="hero-guide-cta">
          <span className="tag">Free guide · Start here</span>
          <h2>The SIBO Starter Guide — everything you need to know</h2>
          <p>Symptoms, testing, and treatment options in one calm, illustrated PDF. Instant download.</p>
          <span className="btn-gold">Get the free guide →</span>
        </Link>

        {/* PRODUCTS */}
        <Divider label="Products" />

        <InternalCard to="/article/sibo-supplements-what-actually-helps-hydrogen-vs-methane-guide" testId="link-supplements" thumbBg="#F0E4C8" title="SIBO Supplements: What Actually Helps" desc="Complete phase-by-phase protocol guide." iconStroke="#93691F">
          <rect x="3.5" y="3.5" width="8" height="17" rx="4" />
          <line x1="3.5" y1="12" x2="11.5" y2="12" />
          <circle cx="17.5" cy="8" r="3.5" />
          <path d="M17.5 4.5v7" />
        </InternalCard>

        <InternalCard to="/resources/probiotics-prebiotics-for-sibo" testId="link-probiotics" thumbBg="#DCE7D3" title="Probiotics & Prebiotics for SIBO" desc="What's safe, what to avoid, and when to start." iconStroke="#3F6B3A">
          <path d="M12 21c-4.5-2-7-5.4-7-9.5C5 7.5 8 4 12 3c4 1 7 4.5 7 8.5 0 4.1-2.5 7.5-7 9.5Z" />
          <path d="M12 8v9" />
          <path d="M12 12c1.8-1.6 3-1.6 4-1" />
        </InternalCard>

        <InternalCard to="/resources/Sibo-four-week-meal-plan" testId="link-meal-plan" thumbBg="var(--links-clay-soft)" title="SIBO 4-Week Meal Plan" desc="The meal plan that helped me rest my gut." iconStroke="#A8695A">
          <rect x="3.5" y="4.5" width="17" height="16" rx="2.5" />
          <line x1="3.5" y1="9.5" x2="20.5" y2="9.5" />
          <line x1="8" y1="3" x2="8" y2="6" />
          <line x1="16" y1="3" x2="16" y2="6" />
          <path d="M8 14.2c.7-.6 1.4-.6 2.1 0s1.4.6 2.1 0 1.4-.6 2.1 0 1.4.6 2.1 0" />
        </InternalCard>

        <InternalCard to="/resources/sibo-diet-food-list" testId="link-food-list" thumbBg="#DCE7D3" title="SIBO Diet Food List" desc="What foods to avoid and which to eat." iconStroke="#3F6B3A">
          <path d="M12 2c-1 3-4 3.5-4 7 0 2.8 1.8 5 4 5s4-2.2 4-5c0-3.5-3-4-4-7Z" />
          <path d="M12 14v8" />
          <path d="M9 22h6" />
        </InternalCard>

        <InternalCard to="/resources/SIBO-Symptoms-Female" testId="link-symptoms-women" thumbBg="#F5DDD9" title="SIBO Symptoms In Women" desc="The signs most doctors miss — and how to recognise them." iconStroke="#B44A5A">
          <circle cx="12" cy="7" r="3.5" />
          <path d="M6.5 20c0-3.5 2.5-6 5.5-6s5.5 2.5 5.5 6" />
          <path d="M9.5 14l-1 6" />
          <path d="M14.5 14l1 6" />
        </InternalCard>

        <InternalCard to="/resources/Sibo-symptom-tracker-30-day" testId="link-symptom-tracker" thumbBg="#EADFDA" title="30-Day SIBO Symptom Tracker" desc="Print, log, and spot the patterns behind your flares." iconStroke="#A8695A">
          <rect x="3.5" y="4" width="17" height="16.5" rx="2.5" />
          <line x1="3.5" y1="9" x2="20.5" y2="9" />
          <line x1="8.5" y1="4" x2="8.5" y2="6" />
          <line x1="15.5" y1="4" x2="15.5" y2="6" />
          <path d="M7 13h3M7 16h3M13 13h4M13 16h4" />
        </InternalCard>

        <InternalCard to="/resources/sibo-Question-Check-List" testId="link-question-checklist" thumbBg="#E4E9DF" title="SIBO Question Checklist" desc="What to ask your doctor at the next appointment." iconStroke="#3F6B3A">
          <rect x="4.5" y="3.5" width="15" height="17" rx="2" />
          <path d="M8 8l1.4 1.4L12.5 6" />
          <path d="M8 13l1.4 1.4L12.5 11" />
          <path d="M8 18l1.4 1.4L12.5 16" />
          <line x1="14" y1="8" x2="17" y2="8" />
          <line x1="14" y1="13" x2="17" y2="13" />
          <line x1="14" y1="18" x2="17" y2="18" />
        </InternalCard>

        {/* AFFILIATES */}
        <Divider label="Tested & trusted" />

        <AffiliateCard href="/article/the-breath-test-explained" internal testId="link-breath-test" emoji="🧪" title="Breath test kit we recommend" desc="The SIBO test that helped me get a diagnosis." />
        <AffiliateCard disabled testId="link-enzyme" emoji="💊" title="Low-FODMAP digestive enzyme" desc="What I personally take with meals during flares. Coming soon." />

        {/* NEWSLETTER */}
        <Divider label="Stay in the loop" />

        <div className="newsletter" data-testid="links-newsletter">
          <h3>One email a week, zero fluff</h3>
          <p>SIBO research, treatment options, and real success stories — to help you stay hopeful through a tough experience.</p>
          <form className="newsletter-form" onSubmit={submit}>
            <input
              type="email"
              placeholder="you@email.com"
              aria-label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="newsletter-email"
            />
            <button type="submit" disabled={sending} data-testid="newsletter-submit">
              {sending ? "…" : "Join"}
            </button>
          </form>
        </div>

        <footer>
          <Link to="/" className="work-with" data-testid="visit-site">Visit siboinstitute.com →</Link>
          <div className="fine-print">
            Some links may be affiliate links. Content is for educational purposes and is not medical advice.
          </div>
        </footer>
      </div>

      <style>{`
        .links-page {
          --links-paper: #EEF1EA;
          --links-paper-deep: #E4E9DF;
          --links-ink: #1C2E29;
          --links-ink-soft: #4A5A54;
          --links-line: #C9D2C4;
          --links-gold: #B9852F;
          --links-gold-deep: #93691F;
          --links-clay: #A8695A;
          --links-clay-soft: #EADFDA;
          --links-focus: #1F3D3A;
          --links-radius: 18px;

          background: var(--cream);
          color: var(--links-ink);
          font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
          -webkit-font-smoothing: antialiased;
          display: flex;
          justify-content: center;
          min-height: 100vh;
        }
        .links-page a { color: inherit; }
        .links-page a:focus-visible, .links-page button:focus-visible {
          outline: 2px solid var(--links-focus);
          outline-offset: 3px;
          border-radius: 8px;
        }

        .links-page .frame {
          width: 100%;
          max-width: 480px;
          min-height: 100vh;
          background: var(--links-paper);
          padding: 40px 24px 64px;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 560px) {
          .links-page .frame {
            margin: 32px 0;
            min-height: auto;
            border-radius: 28px;
            box-shadow: 0 30px 60px -20px rgba(28,46,41,0.25);
          }
        }

        .links-page .mark { width: 46px; height: 46px; margin: 0 auto 18px; display: block; }
        .links-page .eyebrow {
          font-family: 'IBM Plex Mono', ui-monospace, SFMono-Regular, monospace;
          font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase;
          color: var(--links-ink-soft); text-align: center; margin: 0 0 6px;
        }
        .links-page h1.wordmark {
          font-family: 'Fraunces', 'Cormorant Garamond', Georgia, serif;
          font-weight: 600; font-size: 30px; letter-spacing: -0.01em;
          text-align: center; margin: 0 0 10px; color: var(--links-ink);
        }
        .links-page .bio {
          text-align: center; color: var(--links-ink-soft);
          font-size: 14.5px; line-height: 1.55; max-width: 320px;
          margin: 0 auto 22px;
        }
        .links-page .socials {
          display: flex; justify-content: center; gap: 18px; margin-bottom: 34px;
        }
        .links-page .socials a {
          width: 34px; height: 34px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 50%; background: var(--links-paper-deep);
          transition: background .15s ease, transform .15s ease;
        }
        .links-page .socials a:hover { background: var(--links-clay-soft); transform: translateY(-1px); }
        .links-page .socials svg { width: 16px; height: 16px; }

        .links-page .wave-divider {
          display: flex; align-items: center; gap: 12px; margin: 36px 0 18px;
        }
        .links-page .wave-divider svg { flex: 1; height: 14px; overflow: visible; }
        .links-page .wave-divider path {
          fill: none; stroke: var(--links-line); stroke-width: 1.5; stroke-linecap: round;
          stroke-dasharray: 240; stroke-dashoffset: 240;
          animation: linksDraw 1.4s ease forwards;
        }
        .links-page .wave-label {
          font-family: 'IBM Plex Mono', ui-monospace, monospace;
          font-size: 10.5px; letter-spacing: 0.14em; text-transform: uppercase;
          color: var(--links-ink-soft); white-space: nowrap;
        }
        @keyframes linksDraw { to { stroke-dashoffset: 0; } }

        .links-page .card {
          display: block; border-radius: var(--links-radius);
          padding: 18px 20px; margin-bottom: 12px; text-decoration: none;
          border: 1px solid var(--links-line); background: var(--links-paper);
          transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease;
        }
        .links-page .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px -12px rgba(28,46,41,0.25);
        }
        .links-page .card--hero {
          background: linear-gradient(155deg, var(--links-ink) 0%, #24443E 100%);
          color: var(--links-paper); border: none; padding: 24px 22px;
        }
        .links-page .card--hero .tag {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase;
          color: #C9D9B8; display: block; margin-bottom: 10px;
        }
        .links-page .card--hero h2 {
          font-family: 'Fraunces', 'Cormorant Garamond', serif;
          font-weight: 600; font-size: 21px; line-height: 1.28; margin: 0 0 8px;
          color: var(--links-paper);
        }
        .links-page .card--hero p {
          font-size: 13.5px; line-height: 1.55; color: #D7E2D1; margin: 0 0 16px;
        }
        .links-page .btn-gold {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--links-gold); color: #221604;
          font-weight: 600; font-size: 14px;
          padding: 11px 18px; border-radius: 999px;
          text-decoration: none; transition: background .15s ease;
        }
        .links-page .card--hero:hover .btn-gold { background: var(--links-gold-deep); color: var(--links-paper); }

        .links-page .card--std { display: flex; align-items: center; gap: 14px; }
        .links-page .thumb {
          width: 46px; height: 46px; border-radius: 12px;
          flex-shrink: 0; display: flex; align-items: center; justify-content: center;
          background: var(--links-paper-deep); font-size: 19px;
        }
        .links-page .card--std .body { flex: 1; min-width: 0; }
        .links-page .card--std h3 {
          font-family: 'Fraunces', 'Cormorant Garamond', serif;
          font-weight: 500; font-size: 15.5px; margin: 0 0 3px; color: var(--links-ink);
        }
        .links-page .card--std p {
          font-size: 12.5px; color: var(--links-ink-soft);
          margin: 0; line-height: 1.4;
        }
        .links-page .arrow { color: var(--links-ink-soft); font-size: 16px; flex-shrink: 0; }
        .links-page .badge {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 9.5px; letter-spacing: 0.08em; text-transform: uppercase;
          padding: 3px 8px; border-radius: 999px;
          display: inline-block; margin-left: 8px; vertical-align: middle;
        }
        .links-page .badge--affiliate { background: var(--links-clay-soft); color: var(--links-clay); }
        .links-page .card--affiliate .thumb { background: var(--links-clay-soft); }
        .links-page .card.is-disabled { opacity: 0.55; cursor: default; }
        .links-page .card.is-disabled:hover { transform: none; box-shadow: none; }

        .links-page .newsletter {
          border-radius: var(--links-radius);
          border: 1px dashed var(--links-line);
          padding: 20px; text-align: center; margin-top: 6px;
        }
        .links-page .newsletter h3 {
          font-family: 'Fraunces', 'Cormorant Garamond', serif;
          font-weight: 500; font-size: 16px; margin: 0 0 6px; color: var(--links-ink);
        }
        .links-page .newsletter p {
          font-size: 12.5px; color: var(--links-ink-soft);
          margin: 0 0 14px; line-height: 1.5;
        }
        .links-page .newsletter-form { display: flex; gap: 8px; }
        .links-page .newsletter-form input {
          flex: 1; min-width: 0;
          border: 1px solid var(--links-line); background: var(--links-paper);
          border-radius: 999px; padding: 10px 14px;
          font-family: 'Inter', sans-serif; font-size: 13px; color: var(--links-ink);
        }
        .links-page .newsletter-form input::placeholder { color: var(--links-ink-soft); }
        .links-page .newsletter-form button {
          background: var(--links-ink); color: var(--links-paper);
          border: none; border-radius: 999px; padding: 10px 18px;
          font-weight: 600; font-size: 13px; cursor: pointer;
          transition: background .15s ease;
        }
        .links-page .newsletter-form button:hover { background: #24443E; }
        .links-page .newsletter-form button:disabled { opacity: 0.6; cursor: default; }

        .links-page footer { margin-top: 40px; text-align: center; }
        .links-page .work-with {
          font-size: 12.5px; text-decoration: underline; text-underline-offset: 3px;
          color: var(--links-ink-soft); display: inline-block; margin-bottom: 16px;
        }
        .links-page .fine-print {
          font-size: 10.5px; color: var(--links-ink-soft);
          line-height: 1.6; max-width: 340px; margin: 0 auto; opacity: 0.85;
        }

        @media (prefers-reduced-motion: reduce) {
          .links-page .wave-divider path { animation: none; stroke-dashoffset: 0; }
          .links-page .card { transition: none; }
        }
      `}</style>
    </div>
  );
}

function Divider({ label }) {
  return (
    <div className="wave-divider">
      <span className="wave-label">{label}</span>
      <svg viewBox="0 0 200 14" preserveAspectRatio="none">
        <path d="M0 7c10-6 20 6 30 0s20-6 30 0 20 6 30 0 20-6 30 0 20 6 30 0 20-6 30 0" />
      </svg>
    </div>
  );
}

function ThumbIcon({ stroke, children }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="21" height="21" aria-hidden="true">
      {children}
    </svg>
  );
}

function InternalCard({ to, testId, thumbBg, title, desc, iconStroke, children }) {
  return (
    <Link to={to} className="card card--std" data-testid={testId}>
      <div className="thumb" style={{ background: thumbBg }}>
        <ThumbIcon stroke={iconStroke}>{children}</ThumbIcon>
      </div>
      <div className="body">
        <h3>{title}</h3>
        <p>{desc}</p>
      </div>
      <span className="arrow">→</span>
    </Link>
  );
}

function ExternalCard({ href, testId, thumbBg, title, desc, iconStroke, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="card card--std" data-testid={testId}>
      <div className="thumb" style={{ background: thumbBg }}>
        <ThumbIcon stroke={iconStroke}>{children}</ThumbIcon>
      </div>
      <div className="body">
        <h3>{title}</h3>
        <p>{desc}</p>
      </div>
      <span className="arrow">→</span>
    </a>
  );
}

function AffiliateCard({ href, internal, disabled, testId, emoji, title, desc }) {
  // Only show "Affiliate" badge on genuine external affiliate links (FTC-friendly)
  const body = (
    <>
      <div className="thumb" aria-hidden="true">{emoji}</div>
      <div className="body">
        <h3>
          {title} {!internal && !disabled && <span className="badge badge--affiliate">Affiliate</span>}
        </h3>
        <p>{desc}</p>
      </div>
      {!disabled && <span className="arrow">→</span>}
    </>
  );
  if (disabled) {
    return (
      <div className="card card--std card--affiliate is-disabled" data-testid={testId} aria-disabled="true">{body}</div>
    );
  }
  if (internal) {
    return (
      <Link to={href} className="card card--std card--affiliate" data-testid={testId}>{body}</Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer nofollow sponsored" className="card card--std card--affiliate" data-testid={testId}>{body}</a>
  );
}
